// Entry splitting for ChipInput, kept pure and DOM-free so the delimiter rules — the one thing
// the three legacy implementations genuinely disagreed about — are unit-testable on their own.

/**
 * Separators that apply no matter what the caller asked for. None of them can be typed into a
 * single-line `<input>`, so honouring them costs no typing behaviour, and every legacy paste
 * handler already split on them (`[,;\n\t\s]+` for emails, `[,\n\r]+` for keywords).
 */
const ALWAYS_SEPARATE = ['\n', '\r', '\t']

/**
 * Split pasted text into candidate entries.
 *
 * A character scan rather than a built `RegExp`: delimiters are caller data, and interpolating
 * them into a character class needs escaping that a `u`-flagged pattern makes easy to get wrong
 * (`\;` is an identity escape in a plain regex but a `SyntaxError` under `u`). Scanning also has
 * no backtracking profile to reason about.
 *
 * Entries are returned RAW — leading/trailing whitespace intact — because trimming is
 * `ChipInput`'s `normalize` hook's job. Only whitespace-ONLY segments are dropped, which is what
 * makes runs of delimiters (`a,,b`, `a, b`) collapse.
 */
export function splitEntries(text: string, delimiters: readonly string[]): string[] {
  const separators = new Set<string>(ALWAYS_SEPARATE)
  for (const delimiter of delimiters) {
    for (const char of delimiter) separators.add(char)
  }

  const entries: string[] = []
  let current = ''
  for (const char of text) {
    if (!separators.has(char)) {
      current += char
      continue
    }
    if (current.trim()) entries.push(current)
    current = ''
  }
  if (current.trim()) entries.push(current)

  return entries
}
